import React from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/modern/utils';
import { STRIPED_TASK_STYLE } from '@/other/constants/TaskTypes';

interface TaskStylePreviewProps {
  style: string;
  isSelected: boolean;
  color: string;
}

// Helper function to adjust color brightness
export const adjustColorBrightness = (color: string, percent: number): string => {
  const num = parseInt(color.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = (num >> 8 & 0x00FF) + amt;
  const B = (num & 0x0000FF) + amt;
  return '#' + (
    0x1000000 +
    (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
    (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
    (B < 255 ? (B < 1 ? 0 : B) : 255)
  ).toString(16).slice(1);
};

const getStyleClasses = (s: string) => {
  const baseClasses = 'h-6 w-full rounded flex items-center justify-center text-xs';

  switch (s) {
    case 'plain':
      return cn(baseClasses, 'text-white');
    case 'border':
      return cn(baseClasses, 'border-2 bg-transparent');
    case 'hollow':
      return cn(baseClasses, 'border-2 bg-transparent');
    case 'colored':
      return cn(baseClasses, 'text-white');
    case 'line':
      return cn(baseClasses, 'border-b-4 text-foreground bg-transparent');
    case 'dashed':
      return cn(baseClasses, 'border-b-4 border-dashed text-foreground bg-transparent');
    case 'minimal':
      return cn(baseClasses, 'border-l-4 text-foreground bg-transparent');
    case 'rounded':
      return cn(baseClasses, 'text-white rounded-full');
    case STRIPED_TASK_STYLE:
      return cn(baseClasses, 'text-white');
    default:
      return baseClasses;
  }
};

const getStyleInline = (s: string, color: string): React.CSSProperties => {
  const lighterColor = adjustColorBrightness(color, 15);

  switch (s) {
    case 'plain':
    case 'colored':
    case 'rounded':
      return { backgroundColor: color };
    case 'border':
    case 'hollow':
      return { borderColor: color, color: color };
    case 'line':
    case 'dashed':
      return { borderColor: color };
    case 'minimal':
      return { borderColor: color };
    case STRIPED_TASK_STYLE:
      return {
        background: `repeating-linear-gradient(45deg, ${color}, ${color} 10px, ${lighterColor} 10px, ${lighterColor} 20px)`
      };
    default:
      return {};
  }
};

const capitalizeText = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

export const TaskStylePreview: React.FC<TaskStylePreviewProps> = ({ style, isSelected, color }) => {
  return (
    <div className="flex items-center gap-2 w-full">
      <div className={getStyleClasses(style)} style={getStyleInline(style, color)}>
        {capitalizeText(style)}
      </div>
      {isSelected && <Check className="h-4 w-4 ml-auto" />}
    </div>
  );
};
